import { getMasterPointPrivate, getMasterPointPublic } from '@/controllers/gridManager/masterTransManager/masterTransManager';
import { Box, Stack, useTheme } from '@mui/material';
import classNames from 'classnames/bind';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_more from 'highcharts/highcharts-more';
import { useEffect, useState } from 'react';
import styles from './MasterPointChart.module.scss';
import { Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import { useTranslation } from 'react-i18next';
HC_more(Highcharts);

const cx = classNames.bind(styles);

interface MasterPointData {
    investperform: number;
    riskscore: number;
    totalassetcopier: number;
    masterpoint: number;
}

const MasterPointChart = ({ accountId, isPrivate }: { accountId: string, isPrivate?: boolean }) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [data, setData] = useState<MasterPointData>({ investperform: 0, riskscore: 0, totalassetcopier: 0, masterpoint: 0 });

    useEffect(() => {
        const fn = isPrivate === true ? getMasterPointPrivate : getMasterPointPublic
        const loadData = async () => {
            let response = await fn(accountId)
            if (response.isSuccess) setData(response.data)
        }
        loadData()
    }, [accountId, isPrivate])
    const options = {
        chart: {
            polar: true,
            type: 'area',
            backgroundColor: 'transparent'
        },
        title: {
            text: null
        },
        xAxis: {
            categories: [
                t('copytrade.chart.masterPoint.investPerforment'),
                t('copytrade.chart.masterPoint.risk'),
                t('copytrade.chart.masterPoint.totalAsset')
            ],
            tickmarkPlacement: 'on',
            lineWidth: 0,
            labels: {
                useHTML: true,
                formatter: function () {
                    return ''
                },
            },
            gridLineColor: theme.palette.border,
        },
        yAxis: {
            gridLineInterpolation: 'polygon',
            gridLineColor: theme.palette.border,
            lineWidth: 0,
            min: 0,
            tickAmount: 5,
            max: 100,
            labels: {
                enabled: false,
            },
        },
        plotOptions: {
            series: {
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: theme.palette.chart?.series
                    }
                }
            }
        },
        legend: {
            align: 'right',
            verticalAlign: 'middle',
            layout: 'vertical',
            enabled: false,
        },
        series: [{
            data: [data.investperform, data.riskscore, data.totalassetcopier],
            pointPlacement: 'on',
            lineWidth: 0,
            color: '#32D022',
            fillColor: {
                radialGradient: {
                    cx: 0.5,
                    cy: 0.5,
                    r: 0.9
                },
                stops: [
                    [0, '#32D0220F'],
                    [1, '#32D022FF']
                ]
            },
            dataLabels: {
                enabled: false,
            },
        }],
        tooltip: {
            formatter: function () {
                let _this = this as any
                return `${_this.x}: <b>${_this.y}</b>`;
            },
            backgroundColor: theme.palette.secondary.main,
            style: {
                color: theme.palette.text.primary,
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '21px'
            },
        },
        responsive: {
            rules: [{
                condition: {
                    maxWidth: 500
                },
                chartOptions: {
                    title: {
                        x: 0
                    },
                    legend: {
                        align: 'center',
                        verticalAlign: 'bottom',
                        layout: 'horizontal',
                        enabled: false,
                    }
                }
            }]
        },
        exporting: {
            enabled: false,
        },

    };

    return (
        <Stack spacing='15px' p='20px' className={cx("master-point-chart")}>
            <TypographyElement FontWeightElement={700} FontSizeElement='16px' sx={{ lineHeight: '24px' }}>
                {t('copytrade.chart.masterPoint.emasterPoint')}
            </TypographyElement>
            <Stack spacing='20px' direction='row' flex={1}>
                <Stack className={cx("img-line-chart")} alignItems='center'>
                    <Stack zIndex={0} width='100%' height='100%' justifyContent='center' direction='row' position='absolute' top={0}>
                        <Stack alignItems='center' width='100%' height='100%' direction='column'>
                            <Box sx={{
                                aspectRatio: 1.42,
                                width: '100%',
                                maxHeight: '288px',
                                maxWidth: '288px',
                            }}></Box>
                            <Stack direction='row' width='100%' justifyContent='space-between' maxWidth='288px'>
                                <TypographyElement ColorElement='primary' FontWeightElement={400} FontSizeElement='14px' className={cx('dataLabels')} sx={{ maxWidth: '50%' }}>{t('copytrade.chart.masterPoint.totalAsset')}</TypographyElement>
                                <TypographyElement ColorElement='primary' FontWeightElement={400} FontSizeElement='14px' className={cx('dataLabels')} sx={{ maxWidth: '50%' }}>{t('copytrade.chart.masterPoint.risk')}</TypographyElement>
                            </Stack>
                        </Stack>
                    </Stack>
                    <TypographyElement ColorElement='primary' FontWeightElement={400} FontSizeElement='14px' className={cx('dataLabels')}>{t('copytrade.chart.masterPoint.investPerforment')}</TypographyElement>
                    <HighchartsReact containerProps={{ className: cx('master-point__highcharts') }} highcharts={Highcharts} options={options} />
                </Stack>
                <Stack direction='column' minWidth='100px'>
                    <Stack
                        justifyContent='space-between'
                        direction='row'
                        p='5px 10px'
                        width='100%'
                        bgcolor={theme.palette.table?.odd}
                    >
                        <TypographyElement
                            FontSizeElement='14px'
                            FontWeightElement={400}
                            lineHeight='21px'
                        >{t('copytrade.chart.masterPoint.emasterPoint')}</TypographyElement>
                        <TypographyElement
                            FontSizeElement='14px'
                            FontWeightElement={400}
                            lineHeight='21px'
                        >{data.masterpoint}</TypographyElement>
                    </Stack>
                    <Stack
                        justifyContent={'space-between'}
                        direction={'row'}
                        p='5px 10px'
                        width='100%'
                    >
                        <Stack direction='row' alignItems='center'>
                            <TypographyElement
                                FontSizeElement='14px'
                                FontWeightElement={400}
                                lineHeight='21px'
                                ColorElement='secondary'
                            >{t('copytrade.chart.masterPoint.investPerforment')}</TypographyElement>
                            <Tooltip title={
                                <Box sx={{ maxWidth: '200px' }}>
                                    <TypographyElement FontSizeElement='12px' marginBottom='0'>{t('copytrade.chart.masterPoint.investPerformentExplain')}</TypographyElement>
                                    <TypographyElement FontSizeElement='12px' marginBottom='0'>{t('copytrade.chart.masterPoint.density')}: 60%</TypographyElement>
                                </Box>
                            } placement="top">
                                <InfoIcon sx={{ fontSize: '16px', paddingBottom: '2px', marginLeft: '2px', opacity: '0.5', cursor: 'pointer', color: theme.palette.text.secondary }} />
                            </Tooltip>
                        </Stack>
                        <TypographyElement
                            FontSizeElement='14px'
                            FontWeightElement={400}
                            lineHeight='21px'
                        >{data.investperform}</TypographyElement>
                    </Stack>
                    <Stack
                        justifyContent={'space-between'}
                        direction={'row'}
                        p='5px 10px'
                        width='100%'
                    >
                        <Stack direction='row' alignItems='center'>
                            <TypographyElement
                                FontSizeElement='14px'
                                FontWeightElement={400}
                                lineHeight='21px'
                                ColorElement='secondary'
                            >{t('copytrade.chart.masterPoint.risk')}</TypographyElement>
                            <Tooltip title={
                                <Box sx={{ maxWidth: '200px' }}>
                                    <TypographyElement FontSizeElement='12px' marginBottom='0'>{t('copytrade.chart.masterPoint.riskExplain')}</TypographyElement>
                                    <TypographyElement FontSizeElement='12px' marginBottom='0'>{t('copytrade.chart.masterPoint.density')}: 20%</TypographyElement>
                                </Box>
                            } placement="top">
                                <InfoIcon sx={{ fontSize: '16px', paddingBottom: '2px', marginLeft: '2px', opacity: '0.5', cursor: 'pointer', color: theme.palette.text.secondary }} />
                            </Tooltip>
                        </Stack>
                        <TypographyElement
                            FontSizeElement='14px'
                            FontWeightElement={400}
                            lineHeight='21px'
                        >{data.riskscore}</TypographyElement>
                    </Stack>
                    <Stack
                        justifyContent={'space-between'}
                        direction={'row'}
                        p='5px 10px'
                        width='100%'
                    >
                        <Stack direction='row' alignItems='center'>
                            <TypographyElement
                                FontSizeElement='14px'
                                FontWeightElement={400}
                                lineHeight='21px'
                                ColorElement='secondary'
                            >{t('copytrade.chart.masterPoint.totalAsset')}</TypographyElement>
                            <Tooltip title={
                                <Box sx={{ maxWidth: '200px' }}>
                                    <TypographyElement FontSizeElement='12px' marginBottom='0'>{t('copytrade.chart.masterPoint.totalAssetExplain')}</TypographyElement>
                                    <TypographyElement FontSizeElement='12px' marginBottom='0'>{t('copytrade.chart.masterPoint.density')}: 20%</TypographyElement>
                                </Box>
                            } placement="top">
                                <InfoIcon sx={{ fontSize: '16px', paddingBottom: '2px', marginLeft: '2px', opacity: '0.5', cursor: 'pointer', color: theme.palette.text.secondary }} />
                            </Tooltip>
                        </Stack>
                        <TypographyElement
                            FontSizeElement='14px'
                            FontWeightElement={400}
                            lineHeight='21px'
                        >{data.totalassetcopier}</TypographyElement>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default MasterPointChart;